import { Component, computed, inject, input, OnInit, signal } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";

import { AdaaHelper } from "../../../core/utils/adaa-helper";
import { dateRangeValidator } from "../../../core/validators/date-range-validator";
import {
  ConfirmationModalComponent,
  FormActionButtonsComponent,
  FormInputComponent,
  FormInputDateComponent,
  FormTwoWaySelectComponent,
} from "../../../shared/components";
import { Constants } from "../../../shared/constants/constants";
import { PageMode } from "../../../shared/constants/enums";
import { SmartToolTipsDirective } from "../../../shared/directives/smart-tooltips-directive";
import {
  CycleModelType,
  EntityMapModelType,
  EntityModelType,
  ValidatorModelType,
  ValueText,
} from "../../../shared/models";
import {
  CyclesApiService,
  EntitiesApiService,
  LanguageService,
  SystemLayoutService,
  ValidatorApiService,
} from "../../../shared/services";
import { SmartToolTipsService } from "../../../shared/services/smart-tooltips-service";

@Component({
  selector: "adaa-strategic-cycles-editor",
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    FormInputComponent,
    FormTwoWaySelectComponent,
    FormActionButtonsComponent,
    FormInputDateComponent,
    SmartToolTipsDirective,
  ],
  templateUrl: "./strategic-cycles-editor.html",
  styleUrl: "./strategic-cycles-editor.scss",
})
export class StrategicCyclesEditorComponent implements OnInit {
  private _formBuilder = inject(FormBuilder);
  private _entitiesApiService = inject(EntitiesApiService);
  private _cyclesApiService = inject(CyclesApiService);
  private _languageService = inject(LanguageService);
  private _toastrService = inject(ToastrService);
  private _modalService = inject(NgbModal);
  private _router = inject(Router);
  private _smartToolTipsService = inject(SmartToolTipsService);
  private _validatorApiService = inject(ValidatorApiService);
  private _systemLayoutService = inject(SystemLayoutService);

  pageMode = input.required<string>();
  id = input<string>();

  strategicCycleForm: FormGroup = new FormGroup({});
  entities = signal<EntityModelType[]>([]);
  entityOptions = computed(() => {
    return this.entities().map((entity) => {
      return {
        text: AdaaHelper.getItemValueByToken(entity, "name"),
        value: entity.id,
      };
    });
  });
  submitted = signal<boolean>(false);
  cycleButtonLabel = signal<string>("");
  plan = signal<CycleModelType>({
    id: 0,
    nameAE: "",
    nameEN: "",
    dscAE: "",
    dscEN: "",
    startYear: 0,
    endYear: 0,
    dataRevisionId: 0,
    entityMaps: [],
    status: 0,
    wfStatus: "",
    updateTS: 0,
  });
  formValidation = signal<ValidatorModelType | undefined>(undefined);

  PageMode = PageMode;
  Constants = Constants;

  public get displayLabel(): string {
    const nameEN = this.strategicCycleForm.get("nameEN")?.value;
    const nameAE = this.strategicCycleForm.get("nameAE")?.value;

    return AdaaHelper.getPageLabel(nameAE, nameEN);
  }

  public ngOnInit(): void {
    this._prepareForm();
    this._getEntities();
    if (this.pageMode() !== PageMode.view)
      this._smartToolTipsService.initSmartToolTips(Constants.SMART_TOOL_TIPS_KEY.SMART_CYCLE);

    this._systemLayoutService.hasActiveEntityChanged$.subscribe(() => {
      !AdaaHelper.isPMOEntity() && this._router.navigateByUrl("/console");
    });
  }

  private _prepareForm() {
    const isViewMode = this.pageMode() === PageMode.view;

    this.strategicCycleForm = this._formBuilder.group(
      {
        nameAE: { value: null, disabled: isViewMode },
        nameEN: { value: null, disabled: isViewMode },
        dscAE: { value: null, disabled: isViewMode },
        dscEN: { value: null, disabled: isViewMode },
        startYear: { value: null, disabled: isViewMode },
        endYear: { value: null, disabled: isViewMode },
        entityMaps: { value: [], disabled: isViewMode },
      },
      {
        validators: Validators.compose([dateRangeValidator("startYear", "endYear")]),
      }
    );

    this._validatorApiService
      .applyValidationToForm(Constants.VALIDATORS_CONF_KEY.VALIDATION_CYCLES, this.strategicCycleForm)
      .subscribe({
        next: (response) => {
          this.formValidation.set(response.validation);
          this.strategicCycleForm = response.form;
        },
      });
  }

  private _getEntities() {
    this._entitiesApiService.getEntitiesForCycles().subscribe({
      next: (response) => this.entities.set(response.responseData),
      complete: () => {
        const id = this.id();
        if (id) this._getPlan(+id);
      },
    });
  }

  private _getPlan(id: number) {
    this._cyclesApiService.getById(id).subscribe({
      next: (response) => {
        this.plan.set(response.responseData);
        this.strategicCycleForm.patchValue(response.responseData);
        this.cycleButtonLabel.set(this.getLabel(response.responseData.status));
        let result = [];
        for (const value of response.responseData.entityMaps) {
          const entity = this.entities().find((entity) => entity.id === value.entityId);
          if (entity) result.push(entity);
        }
        result = result.map((entity) => entity.id);
        if (this._checkDisabled(false)) {
          this.strategicCycleForm.get("nameAE")?.disable();
          this.strategicCycleForm.get("nameEN")?.disable();
          this.strategicCycleForm.get("startYear")?.disable();
          this.strategicCycleForm.get("endYear")?.disable();
        }
        if (this._checkDisabled(true)) {
          this.strategicCycleForm.get("entityMaps")?.disable();
          this.strategicCycleForm.get("dscAE")?.disable();
          this.strategicCycleForm.get("dscEN")?.disable();
        }
        this.strategicCycleForm.controls["entityMaps"].patchValue(result);
        this.strategicCycleForm.markAsPristine();
        this.strategicCycleForm.markAsUntouched();
      },
    });
  }

  public submit(updateStatus: boolean = false) {
    this.submitted.set(true);

    if (this.strategicCycleForm.invalid) {
      this._toastrService.error(this._languageService.translate("common.form.label.missing_information"));
      return;
    }

    const formValue = this.strategicCycleForm.value;

    if (this.pageMode() === PageMode.edit) {
      formValue.entityMaps = formValue.entityMaps.map((item: ValueText | number) =>
        typeof item === "number" ? item : item.value
      );

      const entityMaps = this._buildEntityMaps(formValue);

      let newStatus: number = 0;
      if (this.plan().status === Constants.OBJECT_STATUS.ACTIVE) {
        newStatus = Constants.OBJECT_STATUS.CLOSED;
      } else if (
        this.plan().status === Constants.OBJECT_STATUS.CLOSED ||
        this.plan().status === Constants.OBJECT_STATUS.PLANNING
      ) {
        newStatus = Constants.OBJECT_STATUS.ACTIVE;
      }

      const result = {
        ...this.plan(),
        ...formValue,
        entityMaps: entityMaps,
        status: updateStatus ? newStatus : this.plan().status,
      };
      this._cyclesApiService.updatePlan(result).subscribe({
        next: () => {
          this._toastrService.success(this._languageService.translate("notification.success.save"));
          this._router.navigateByUrl("console/cycles");
        },
      });
    } else {
      formValue.entityMaps = formValue.entityMaps.map((entityId: number) => {
        return {
          entityId,
          startYear: formValue.startYear,
          endYear: formValue.endYear,
          status: Constants.OBJECT_STATUS.DRAFT,
        };
      });
      this._cyclesApiService.createPlan(formValue).subscribe({
        next: () => {
          this._toastrService.success(this._languageService.translate("notification.success.save"));
          this._router.navigateByUrl("console/cycles");
        },
      });
    }
  }

  public cancel() {
    if (this.strategicCycleForm.dirty) {
      const modal = this._modalService.open(ConfirmationModalComponent, {
        centered: true,
        size: "md",
        modalDialogClass: this._languageService.modalDirection(),
      });

      modal.componentInstance.header = "common.form.modals_leave.cancel_yes_no_title";
      modal.componentInstance.title = "common.form.modals_leave.cancel_yes_no_information";

      modal.result.then((e) => {
        if (e) this._router.navigateByUrl(`/console/cycles`);
      });
    } else this._router.navigateByUrl("/console/cycles");
  }

  public checkInvalid(control: string) {
    return (
      this.strategicCycleForm.controls[control].errors?.["required"] &&
      (this.strategicCycleForm.get(control)?.dirty || this.submitted())
    );
  }

  private _checkDisabled(isDescription: boolean) {
    let res: boolean;
    if (this.pageMode() === PageMode.view) return true;
    if (this.pageMode() === PageMode.edit) {
      if (isDescription) {
        res = this.plan().status === Constants.OBJECT_STATUS.CLOSED;
      } else res = this.plan().status !== Constants.OBJECT_STATUS.PLANNING;
      return res;
    }
    return null;
  }

  public getLabel(status: number) {
    switch (status) {
      case Constants.OBJECT_STATUS.ACTIVE:
        return "cycle.close_cycle";
      case Constants.OBJECT_STATUS.CLOSED:
        return "cycle.reopen_cycle";
      case Constants.OBJECT_STATUS.PLANNING:
        return "cycle.activate_cycle";
      default:
        return "";
    }
  }

  private _buildEntityMaps(
    formValue: ReturnType<typeof this.strategicCycleForm.getRawValue>
  ): Partial<EntityMapModelType>[] {
    const existingMaps = this.plan().entityMaps;

    const entityMaps: Partial<EntityMapModelType>[] = (formValue.entityMaps as number[]).map((entityId) => {
      const entity = existingMaps.find((val) => val.entityId === entityId);
      return (
        entity ?? {
          entityId,
          startYear: formValue.startYear,
          endYear: formValue.endYear,
          status: Constants.OBJECT_STATUS.DRAFT,
        }
      );
    });

    const removedMaps = existingMaps
      .filter((entity) => !entityMaps.some((map) => map.id === entity.id))
      .map((entity) => ({ ...entity, status: Constants.OBJECT_STATUS.REMOVE }));

    return [...entityMaps, ...removedMaps];
  }
}
